import type { Theme } from "@mui/material";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import { DIALOG_WIDTH } from "../../constants";
import { CloseDialogHandler } from "../../contexts/DialogContext";
import { useUpdateUser } from "../../hooks/mutation/update/useUpdateUser";
import { useUsers } from "../../hooks/query/useUsers";
import { Schemas } from "../../schemas";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";

const schema = Schemas.user.omit({ email: true });

type FormValues = z.input<typeof schema>;

const EditUserDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
  userId: string;
}> = ({ onDialogClosed, userId }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const normalizedUserId = React.useMemo(() => userId.trim(), [userId]);
  const { data: users = [], isLoading: isLoadingUsers } = useUsers();
  const { mutateAsync: updateUser, isPending: isUpdating } = useUpdateUser();
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const user = React.useMemo(() => {
    return users.find((currentUser) => {
      return currentUser.id === normalizedUserId;
    });
  }, [normalizedUserId, users]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      role: "admin",
      status: "pending",
    },
    mode: "onBlur",
  });

  React.useEffect(() => {
    if (!user) {
      return;
    }

    reset({
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      status: user.status,
    });
  }, [reset, user]);

  const onSubmit = async (values: FormValues) => {
    try {
      if (!user) {
        setSubmitError("User not found.");
        return;
      }
      setSubmitError(null);
      await updateUser({
        userId: normalizedUserId,
        user: {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          role: values.role,
          status: values.status,
        },
      });
      onDialogClosed({ message: "User updated!", severity: "success" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onInvalid = (errs: Record<string, unknown>) => {
    console.error(errs);
    setSubmitError("Error during submit");
  };

  const onClose = () => {
    if (isUpdating) {
      return;
    }
    setSubmitError(null);
    reset({ firstName: "", lastName: "", role: "admin", status: "pending" });
    onDialogClosed();
  };

  const userNotFound = !isLoadingUsers && !user;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg } }}
    >
      <StandardDialogTitle title={"Edit User"} isSmallScreen={isSmallScreen} onClose={onClose} />

      <Box component="form" id="edit-user-form" onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Update user profile information and access status.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={2.25} sx={{ width: "100%" }}>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <TextField size="small" value={user?.email ?? ""} disabled />
                <FormHelperText>{" "}</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>First Name</FormLabel>
                <TextField
                  size="small"
                  error={!!errors.firstName}
                  {...register("firstName", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.firstName}>
                  {errors.firstName?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Last Name</FormLabel>
                <TextField
                  size="small"
                  error={!!errors.lastName}
                  {...register("lastName", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.lastName}>
                  {errors.lastName?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Role</FormLabel>
                <TextField
                  size="small"
                  select
                  error={!!errors.role}
                  {...register("role", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                >
                  <MenuItem value="admin">admin</MenuItem>
                </TextField>
                <FormHelperText error={!!errors.role}>
                  {errors.role?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <TextField
                  size="small"
                  select
                  error={!!errors.status}
                  {...register("status", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                >
                  <MenuItem value="pending">pending</MenuItem>
                  <MenuItem value="confirmed">confirmed</MenuItem>
                </TextField>
                <FormHelperText error={!!errors.status}>
                  {errors.status?.message ?? " "}
                </FormHelperText>
              </FormControl>

              {userNotFound && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  User not found.
                </FormHelperText>
              )}

              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText={"Save"}
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isUpdating}
          disabled={!user || isUpdating}
        />
      </Box>
    </Dialog>
  );
};

export default EditUserDialog;
